import type { CalculationResultResponseType } from '../Connection/types';

export class CalculationResult {
    private _cr: CalculationResultResponseType | null = null;

    set(cr: CalculationResultResponseType): this {
        this._cr = cr;
        return this;
    }

    get(): CalculationResultResponseType | null {
        return this._cr;
    }

    exists() {
        return !!this._cr;
    }

    getId(): number | null {
        return this._cr?.id ?? null;
    }

    getConnectionId(): number | null {
        if (!this._cr?.connection_id) {
            return null;
        }
        return this._cr.connection_id;
    }

    getRouteTypeId(): number {
        if (!this._cr?.route_type_id) {
            throw new Error('CalculationResult.getRouteTypeId: route_type_id is not set');
        }
        return this._cr.route_type_id;
    }

    getRouteMeters(): number | null {
        return (
            this.getOtherRoadsMeters() +
            (this.getCountryRoadsMeters() ?? 0) +
            (this.getExpressRoadsMeters() ?? 0) +
            (this.getHighwayRoadsMeters() ?? 0)
        );
    }

    getOtherRoadsMeters(): number {
        let countryRoadsMeters = this.getCountryRoadsMeters();
        let expressRoadsMeters = this.getExpressRoadsMeters();
        let highwayRoadsMeters = this.getHighwayRoadsMeters();
        let totalMeters = this._cr?.route_length ?? null;
        if (totalMeters === null) {
            return 0;
        }
        let output: number = totalMeters - (countryRoadsMeters ?? 0) - (expressRoadsMeters ?? 0) - (highwayRoadsMeters ?? 0);
        return Math.max(0, output);
    }

    getEtollPrice(): number | null {
        return this._cr?.etoll_price ?? null;
    }

    getAuCat1(): number | null {
        return this._cr?.au_cat1 ?? null;
    }

    getAuCat2(): number | null {
        return this._cr?.au_cat2 ?? null;
    }

    getAuCat3(): number | null {
        return this._cr?.au_cat3 ?? null;
    }

    getAuCat4(): number | null {
        return this._cr?.au_cat4 ?? null;
    }

    getCountryRoadsMeters(): number | null {
        return this._cr?.country_roads_length ?? null;
    }

    getExpressRoadsMeters(): number | null {
        return this._cr?.express_roads_length ?? null;
    }

    getHighwayRoadsMeters(): number | null {
        return this._cr?.highway_roads_length ?? null;
    }

    getCountryRoadsTimeSeconds(): number | null {
        return this._cr?.country_roads_time ? this._cr?.country_roads_time * 60 : null;
    }

    getExpressRoadsTimeSeconds(): number | null {
        return this._cr?.express_roads_time ? this._cr?.express_roads_time * 60 : null;
    }

    getHighwayRoadsTimeSeconds(): number | null {
        return this._cr?.highway_roads_time ? this._cr?.highway_roads_time * 60 : null;
    }

    getTotalTimeSeconds(): number | null {
        return (
            this.getOtherRoadsTimeSeconds() +
            (this.getCountryRoadsTimeSeconds() ?? 0) +
            (this.getExpressRoadsTimeSeconds() ?? 0) +
            (this.getHighwayRoadsTimeSeconds() ?? 0)
        );
    }

    public getOtherRoadsTimeSeconds(): number {
        let countryRoadsTimeSeconds = this.getCountryRoadsTimeSeconds();
        let expressRoadsTimeSeconds = this.getExpressRoadsTimeSeconds();
        let highwayRoadsTimeSeconds = this.getHighwayRoadsTimeSeconds();
        let totalSeconds = this._cr?.total_time ? this._cr?.total_time * 60 : null;
        if (totalSeconds === null) {
            return 0;
        }
        let output = totalSeconds - (countryRoadsTimeSeconds ?? 0) - (expressRoadsTimeSeconds ?? 0) - (highwayRoadsTimeSeconds ?? 0);
        return Math.max(0, output);
    }
}
